export class CanvasStringConstants {
  public static readonly DEFAULT_CHAT_MESSAGE = 'Please enter a message...';
  public static readonly DIRTY_CANVAS_SAVE_TILE = 'Unsaved changes';
  public static readonly DIRTY_CANVAS_SAVE_MESSAGE =
    'You have unsaved changes in canvas. Do you want to save them before continuing?';
  public static readonly DIRTY_CANVAS_DISCARD_MESSAGE =
    'You have unsaved changes in canvas. Do you want to discard them before continuing?';
  public static readonly DIRTY_CANVAS_SAVE_BUTTON = 'Save changes';
  public static readonly DIRTY_CANVAS_CANCEL_BUTTON = 'Dont save & continue';
  public static readonly DIRTY_CANVAS_DISCARD_CONTINUE_BUTTON =
    'Discard changes & continue';
  public static readonly DIRTY_CANVAS_DISCARD_CANCEL_BUTTON = 'Keep working';
  public static readonly INLINE_LOADER_TEXT =
    'Hang tight! AI is crafting your response';
  public static readonly ROBOT_LOADER_TEXT =
    'Generating content, please wait...';
  public static readonly REMOVE_SHARE_USER_TITLE = 'Remove User';
  public static readonly REMOVE_SHARE_USER_MESSAGE =
    'Are you sure you want to remove this user from the shared list?';
  public static readonly REMOVE_SHARE_USER_PROCEED_BUTTON = 'Remove User';
  public static readonly REMOVE_SHARE_USER_CANCEL_BUTTON = 'Cancel';
  public static readonly TEMPORARY_DOCUMENT_ID = 'temp_document_id';

  public static readonly CONVERSATION_LIMIT_WARNING_TITLE =
    'Conversation Limit Warning';
  public static readonly CONVERSATION_LIMIT_WARNING_MESSAGE =
    'You are approaching the maximum number of messages allowed in this conversation. Please consider starting a new conversation to continue your discussion.';
  public static readonly CONVERSATION_LIMIT_REACHED_TITLE =
    'Conversation Limit Exceeded';
  public static readonly CONVERSATION_LIMIT_REACHED_MESSAGE =
    'You have exceeded the maximum number of messages allowed in this conversation. Please start a new conversation to continue your discussion or check other sessions.';
  public static readonly CONVERSATION_LIMIT_CANCEL_BUTTON = 'Close';
  public static readonly CONVERSATION_LIMIT_WARNING_BUTTON = 'Continue';

  public static readonly FAILED_TO_LOAD_SESSIONS_TITLE =
    'Failed to Load Sessions';
  public static readonly FAILED_TO_LOAD_SESSIONS_MESSAGE =
    'There was an error loading the sessions. Please try again later.';
  public static readonly FAILED_TO_LOAD_SESSIONS_BUTTON = 'Close';
  public static readonly SHARED_LIMIT_REACHED_TITLE = 'Shared Limit Warning';
  public static readonly SHARED_LIMIT_REACHED_MESSAGE =
    'You have exceeded the maximum number of sharing allowed for this conversation.';
}

export interface AttachedChatFile {
  file_name: string;
  dataset_name: string;
  description: string;
  datasource_name: string;
  query_instructions: string;
  linked_sections: any;
  file?: any;
  metadata?: any;
  id?: number;
}

export interface SharedUser {
  permission:
    | ShareAccessLevel.READ_ONLY
    | ShareAccessLevel.CONTRIBUTE
    | ShareAccessLevel.MANAGE_SESSION;
  user_email: string;
  user_id: string;
  shared_by: string;
  temporary_user?: boolean;
}
export interface Session {
  bookmarked: boolean;
  created_on: string;
  displayDate: string;
  is_active: boolean;
  isSaving: boolean;
  isMySession: boolean;
  permission:
    | ShareAccessLevel.READ_ONLY
    | ShareAccessLevel.CONTRIBUTE
    | ShareAccessLevel.MANAGE_SESSION;
  organization_id: string;
  title: string;
  user_id: string;
  _id: string | undefined;
  highlight: boolean;
  shared_with: SharedUser[];
}

export interface AgentWorking {
  isAgentWorking: boolean;
  isAgentRegenerating: boolean;
  conversation_id?: string;
  session_id?: string;
}

export enum SessionViewType {
  MY_SESSIONS = 'my_sessions',
  SESSIONS_SHARED_WITH_ME = 'sessions_shared_with_me',
}

export enum ToolbarOriginType {
  BY_MOUSE_SELECTION = 'selection',
  BY_SECTION_TITLE_CLICK = 'dialog',
}

export enum ConverstationStatus {
  IN_PROGRESS = 'Inprogress',
  COMPLETED = 'Completed',
}

export enum ShareAccessLevel {
  READ_ONLY = 'read',
  CONTRIBUTE = 'contribute',
  MANAGE_SESSION = 'manage',
}

export enum CanvasViewTypes {
  Canvas = 'canvas-editor',
  Comparison = 'compare-version',
}

export const ChatMessageType = {
  USER_MESSAGE: 'user',
  AGENT_MESSAGE: 'agent',
} as const;

export type ChatMessageType =
  (typeof ChatMessageType)[keyof typeof ChatMessageType];

export const SockeConnectionType = {
  CHAT: 'chat',
  REGENERATE: 'regenerate',
  CANVAS_CHAT: 'canvas_chat',
} as const;

export type SockeConnectionType =
  (typeof SockeConnectionType)[keyof typeof SockeConnectionType];

export const ConnectionReasons = {
  SUCCESS: 'success',
  FAILURE: 'failure',
  ERROR: 'error',
  MANUAL: 'manual',
  RETRIES_EXCEEDED: 'retries_exhausted',
  INVALID_MESSAGE: 'invalid_message_format',
} as const;

export type ConnectionReasons =
  (typeof ConnectionReasons)[keyof typeof ConnectionReasons];

export const SocketConnectionStage = {
  CONNECT: 'connect',
  MESSAGE: 'message',
  DISCONNECT: 'disconnect',
  ERROR: 'disconnected_on_error',
  COMPLETE: 'complete',
} as const;

export type SocketConnectionStage =
  (typeof SocketConnectionStage)[keyof typeof SocketConnectionStage];

export const SocketResponseMessageType = {
  SECTION_CONTENT: 'section_content',
  AGENT_STATUS_UPDATE: 'agent_status_update',
  AGENT_CHAT_DOCUMENT_RESPONSE: 'agent_chat_document_response',
  CHAT_INITIALIZED: 'agent_chat_initialize',
  AGENT_RESPONSE: 'agent_response',
  AGENT_FAILED: 'generation_failed',
} as const;

export type SocketResponseMessageType =
  (typeof SocketResponseMessageType)[keyof typeof SocketResponseMessageType];

export interface SocketEvent {
  type: SocketConnectionStage;
  payload?: any;
}

export class AgentStatusUpdate {
  public agentCurrentStatus!: string;
}

export interface SocketPayload {
  data: any;
  type: SockeConnectionType;
}

export interface ConversationSettings {
  conversation_warning_limit: number;
  conversation_max_limit: number;
  session_share_max_limit: number;
}

export interface SocketMessage {
  messageType?: string;
  response: string | ChatResponse;
  messageId?: string;
  sessionId?: string;
  conversationId?: string;
  operation_type?: string;
}

export interface ChatResponse {
  created_on: string;
  document_id: string;
  response_text: string;
  document: any;
  scorecard_summary: any;
  sources: any[];
  stats: StatResponse;
}

export interface StatResponse {
  total_time: number;
  usage_cost: number;
  usage_tokens: number;
}

export class ChatExpansion {
  public static readonly FULL_SCREEN_EXPANDED = true;
  public static readonly SMALL_SCREEN_LEFT = false;
}

export const CanvasLoaderType = {
  TYPING: 'typing',
  ROBOT: 'robot',
  ROBOT_ERROR: 'failure',
  ROBOT_LOADING: 'loading',
} as const;

export type CanvasLoaderType =
  (typeof CanvasLoaderType)[keyof typeof CanvasLoaderType];

export enum CanvasEvents {
  INIT = 'INIT',
  SOCKET_INIT = 'SOCKET_INIT',
  CLEAR_INSTANCE = 'CLEAR_INSTANCE',
  FETCH_SESSIONS = 'FETCH_SESSIONS',
  FETCH_SESSIONS_SUCCESS = 'FETCH_SESSIONS_SUCCESS',
  FETCH_SESSIONS_ERROR = 'FETCH_SESSIONS_ERROR',
  FETCH_CHAT_MESSAGES = 'FETCH_CHAT_MESSAGES',
  FETCH_CHAT_MESSAGES_SUCCESS = 'FETCH_CHAT_MESSAGES_SUCCESS',
  FETCH_CHAT_MESSAGES_ERROR = 'FETCH_CHAT_MESSAGES_ERROR',
  NEW_SESSION_CREATED = 'NEW_SESSION_CREATED',
  SESSION_HIGHLIGHTED = 'SESSION_HIGHLIGHTED',
  FETCH_FILES = 'FETCH_FILES',
  FETCH_FILES_SUCCESS = 'FETCH_FILES_SUCCESS',
  FETCH_FILES_ERROR = 'FETCH_FILES_ERROR',
  DELETE_SESSION = 'DELETE_SESSION',
  DELETE_SESSION_SUCCESS = 'DELETE_SESSION_SUCCESS',
  DELETE_SESSION_SUCCESS_WITHOUT_HIGHLIGHT = 'DELETE_SESSION_SUCCESS_WITHOUT_HIGHLIGHT',
  DELETE_SESSION_ERROR = 'DELETE_SESSION_ERROR',
  CONVERSATION_NAVIGATION_TRIGGERED = 'CONVERSATION_NAVIGATION_TRIGGERED',
  RESPONSE_BOOKMARKED = 'RESPONSE_BOOKMARKED',
  RESPONSE_UNBOOKMARKED = 'RESPONSE_UNBOOKMARKED',
  CANVAS_DOCUMENT_FETCHED = 'CANVAS_DOCUMENT_FETCHED',
  CANVAS_DOCUMENT_UPDATED = 'CANVAS_DOCUMENT_UPDATED',
  CANVAS_DOCUMENT_FETCHED_ERROR = 'CANVAS_DOCUMENT_FETCHED_ERROR',
}

export enum ChatMessageConstants {
  CONNECTING = 'CONNECTING',
  OPEN = 'OPEN',
  RECONNECTING = 'RECONNECTING',
  FAILED = 'FAILED',
  DISCONNECTED = 'DISCONNECTED',
  MESSAGE_SENT_SUCCESS = 'Message sent successfully',
  MESSAGE_SENT_FAILED = 'Failed to send message',
  WEBSOCKET_ERROR = 'WebSocket error occurred',
  WEBSOCKET_CLOSED = 'WebSocket connection closed',
}

export enum ChatTypes {
  CONTEXT = 'context',
  CHAT = 'chat',
  REGENERATE = 'regenerate',
  SCORECARD = 'scorecard',
  PROJECT_NARRATIVE = 'project narrative',
  OUTLINE = 'outline',
  SECTION = 'section',
  CANVAS_UPDATE = 'update',
  CANVAS_CHAT = 'canvas_action',
}

export enum SCORE_CARD_VALUES {
  OUTSTANDING = 'Outstanding',
  VERY_GOOD = 'Very Good',
  ACCEPTABLE = 'Acceptable',
  MARGINAL = 'Marginal',
  UNACCEPTABLE = 'Unacceptable',
}

export enum SCORE_CARD_DESCRIPTION {
  OUTSTANDING = 'All criteria are thoroughly addressed, strongly developed, and well supported. Documentation and required information are specific and comprehensive. Application is extremely strong with insignificant weaknesses. Weaknesses identified will likely have no impact on the successful implementation of the proposed project.',
  VERY_GOOD = 'Criteria are thoroughly addressed with necessary detail and clearly supported. Documentation and required information are specific and feasible. Application is very strong with only some minor weaknesses. Weaknesses identified will likely have minor impact on the successful implementation of the proposed project.',
  ACCEPTABLE = 'Criteria are addressed, but do not contain necessary detail and/or support. Most documentation and required information are present and sufficient, although some are deficient or missing. Application has some strengths but with at least one major weakness. Weaknesses identified will likely have moderate impact on the successful implementation of the proposed project.',
  MARGINAL = 'Some criteria are addressed, although when addressed, do not contain necessary detail and/or support. Some documentation and required information are missing or deficient. Application has a few strengths and a few major weaknesses. Weaknesses identified will likely impact the successful implementation of the proposed project.',
  UNACCEPTABLE = 'Few, if any, criteria are addressed. Documentation and required information are missing. Application has very few strengths and numerous major weaknesses. Weaknesses identified will likely prevent the successful implementation of the proposed project.',
}

export interface FeedbackObject {
  feedback_type: 'positive' | 'negative';
  feedback_text: string;
  categories?: string[];
  conversation_id: string;
  response_id: string;
  session_id?: string;
}
